import { useMemo, useState, type KeyboardEvent } from "react";
import { Icon } from "./Icon";
import { Button, FieldLabel, Input } from "./ui";
import { cn } from "../lib/cn";
import { estimateEvalPlan, formatChars } from "../lib/evalClusterPlan";
import type { EvalCluster, EvalClusterDocument } from "../lib/evalPlaygroundStorage";

const MAX_MATCHES = 50;

function clusterMeta(c: EvalCluster): string {
  const docs = `${c.document_count} document${c.document_count === 1 ? "" : "s"}`;
  const projects = `${c.project_count} project${c.project_count === 1 ? "" : "s"}`;
  return `${docs} · ${projects}`;
}

type Props = {
  clusters: EvalCluster[];
  clustersLoading: boolean;
  cluster: EvalCluster | null;
  onSelect: (bucketId: string) => void;
  documents: EvalClusterDocument[];
  documentsLoading: boolean;
  checkedIds: string[];
  onCheckedChange: (ids: string[]) => void;
  modelCount: number;
  maxCallsPerModel: number;
};

export function VendorClusterPicker({
  clusters,
  clustersLoading,
  cluster,
  onSelect,
  documents,
  documentsLoading,
  checkedIds,
  onCheckedChange,
  modelCount,
  maxCallsPerModel,
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () => [...clusters].sort((a, b) => a.label.localeCompare(b.label)),
    [clusters],
  );
  const matches = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return sorted;
    return sorted.filter((c) => {
      const hay = `${c.label} ${c.target_company} ${c.contractor}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [sorted, query]);

  const checked = useMemo(() => new Set(checkedIds), [checkedIds]);
  const checkedDocs = useMemo(
    () => documents.filter((d) => checked.has(d.evidence_id)),
    [documents, checked],
  );
  const plan = useMemo(
    () => estimateEvalPlan(checkedDocs, cluster?.contractor ?? "", maxCallsPerModel),
    [checkedDocs, cluster?.contractor, maxCallsPerModel],
  );

  const choose = (bucketId: string) => {
    onSelect(bucketId);
    setQuery("");
    setOpen(false);
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && matches.length) {
      e.preventDefault();
      choose(matches[0].bucket_id);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const toggle = (id: string, on: boolean) => {
    const next = new Set(checked);
    if (on) next.add(id);
    else next.delete(id);
    // Keep the cluster's document order: it is the order the calls read them in.
    onCheckedChange(documents.filter((d) => next.has(d.evidence_id)).map((d) => d.evidence_id));
  };

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <FieldLabel>Vendor cluster</FieldLabel>
      <div
        className="relative"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <Input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onSearchKey}
          placeholder={
            clustersLoading
              ? "Loading vendor clusters…"
              : `Search ${clusters.length} vendor clusters by target or vendor…`
          }
          aria-label="Search vendor clusters"
          aria-expanded={open}
          aria-controls="eval-cluster-matches"
        />
        {open && !clustersLoading ? (
          <ul
            id="eval-cluster-matches"
            className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto custom-scrollbar rounded border border-outline-variant bg-surface-container-high shadow-lg divide-y divide-outline-variant"
          >
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-body-sm text-on-surface-variant">
                No vendor cluster matches “{query}”.
              </li>
            ) : (
              matches.slice(0, MAX_MATCHES).map((c) => (
                <li key={c.bucket_id}>
                  <button
                    type="button"
                    onClick={() => choose(c.bucket_id)}
                    className={cn(
                      "w-full text-left px-3 py-2 hover:bg-primary/10 focus:bg-primary/10 focus:outline-none",
                      cluster?.bucket_id === c.bucket_id && "bg-primary/10",
                    )}
                  >
                    <span className="block text-body-sm text-on-surface truncate">{c.label}</span>
                    <span className="block text-[10px] font-data-mono text-on-surface-variant">
                      {clusterMeta(c)}
                    </span>
                  </button>
                </li>
              ))
            )}
            {matches.length > MAX_MATCHES ? (
              <li className="px-3 py-2 text-[10px] uppercase tracking-wide text-on-surface-variant">
                {matches.length - MAX_MATCHES} more: refine the search
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      {cluster ? (
        <div className="rounded border border-outline-variant bg-surface-container-low">
          <div className="px-3 py-2 border-b border-outline-variant flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-body-sm font-semibold text-on-surface">{cluster.label}</span>
            <span className="text-[10px] font-data-mono text-on-surface-variant">
              {clusterMeta(cluster)}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wide text-on-surface-variant">
                {checkedDocs.length} of {documents.length} ticked
              </span>
              <Button
                variant="link"
                size="sm"
                disabled={documentsLoading}
                onClick={() => onCheckedChange(documents.map((d) => d.evidence_id))}
              >
                All
              </Button>
              <Button
                variant="link"
                size="sm"
                disabled={documentsLoading}
                onClick={() => onCheckedChange([])}
              >
                None
              </Button>
            </span>
          </div>
          {documentsLoading ? (
            <p className="px-3 py-2 text-body-sm text-on-surface-variant">Loading documents…</p>
          ) : documents.length === 0 ? (
            <p className="px-3 py-2 text-body-sm text-on-surface-variant">
              No extracted documents in this cluster.
            </p>
          ) : (
            <ul className="max-h-56 overflow-y-auto custom-scrollbar divide-y divide-outline-variant">
              {documents.map((d) => (
                <li key={d.evidence_id} className="flex items-center gap-2 px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={checked.has(d.evidence_id)}
                    onChange={(e) => toggle(d.evidence_id, e.target.checked)}
                    aria-label={`Include ${d.title || d.url}`}
                  />
                  <span
                    className="flex-1 min-w-0 truncate text-body-sm text-on-surface"
                    title={d.url || d.title}
                  >
                    {d.title || d.url || d.evidence_id}
                  </span>
                  <span className="shrink-0 text-[10px] font-data-mono text-on-surface-variant">
                    {formatChars(d.chars)} chars
                  </span>
                  {d.url ? (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-on-surface-variant hover:text-primary"
                      title="Open source"
                    >
                      <Icon name="open_in_new" className="text-sm" />
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {checkedDocs.length > 0 ? (
            <p
              className={cn(
                "px-3 py-2 border-t border-outline-variant text-[11px] font-data-mono",
                plan.unread || plan.partial ? "text-tertiary" : "text-on-surface-variant",
              )}
            >
              {formatChars(plan.chars)} chars → ≈ {plan.calls} call{plan.calls === 1 ? "" : "s"} per
              model
              {modelCount > 1 ? ` × ${modelCount} models` : ""}
              {plan.unread || plan.partial
                ? ` · only the first ${maxCallsPerModel} calls run: ≈ ${plan.unread} document${
                    plan.unread === 1 ? "" : "s"
                  } unread${plan.partial ? `, ${plan.partial} partly read` : ""}. Untick some to fit.`
                : ""}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
